import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';

import { ResourceTypeDto } from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetCustomerDetailsBusiness } from '@/views/customerDetails/components/business/util/useGetCustomerDetailsBusiness.util.ts';
import { useGetCustomerDetailsIndividual } from '@/views/customerDetails/components/individual/util/useGetCustomerDetailsIndividual.util.ts';
import { useGetCustomerType } from '@/views/customerDetails/util/useGetCustomerType.util.ts';
import { useGetSupplierDetails } from '@/views/suppliers/details/util/useGetSupplier.util.ts';

type Props = {
  resourceId?: number;
  resourceType: string;
};

export const ContactResourceDetails: React.FC<Props> = props => {
  const { resourceId, resourceType } = props;
  const { customerType, getCustomerType, isGettingCustomerType } = useGetCustomerType();

  useEffect(() => {
    if (!resourceId || resourceType !== ResourceTypeDto.Customer) {
      return;
    }
    void getCustomerType({ customerId: resourceId });
  }, [resourceId, resourceType]);

  if (!resourceId) {
    return null;
  }

  if (resourceType === ResourceTypeDto.Supplier) {
    return <SupplierDetails supplierId={resourceId} />;
  }

  if (isGettingCustomerType) {
    return <Loader />;
  }

  if (customerType === 'INDIVIDUAL') {
    return <CustomerIndividualDetails customerId={resourceId} />;
  }

  return <CustomerBusinessDetails customerId={resourceId} />;
};

const CustomerIndividualDetails = (props: { customerId: number }) => {
  const { customerId } = props;
  const { customerDetails, isGettingCustomerDetails } = useGetCustomerDetailsIndividual({
    customerId,
  });
  return (
    <CustomerDetails customerName={customerDetails?.name} isLoading={isGettingCustomerDetails} />
  );
};

const CustomerBusinessDetails = (props: { customerId: number }) => {
  const { customerId } = props;
  const { customerDetails, isGettingCustomerDetails } = useGetCustomerDetailsBusiness({
    customerId,
  });
  return (
    <CustomerDetails customerName={customerDetails?.name} isLoading={isGettingCustomerDetails} />
  );
};

const CustomerDetails = (props: { customerName?: string; isLoading: boolean }) => {
  const { t } = useTranslationWithPrefix('views.contactsCreate.customerDetails');
  const { customerName, isLoading } = props;

  if (isLoading) {
    return <Loader />;
  }
  return (
    <>
      <Typography sx={{ color: themeColors.greyDark }} variant="f16Regular">
        {t('addingResourceFor', { forResource: t('forCustomer') })}
      </Typography>
      <Typography sx={{ color: themeColors.greyDark, pl: 1 }} variant="f16Medium">
        {customerName}
      </Typography>
    </>
  );
};

const Loader: React.FC = () => {
  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <CircularProgress sx={{ color: themeColors.accentColor }} />
    </Box>
  );
};

const SupplierDetails = (props: { supplierId: number }) => {
  const { supplierId } = props;
  const { t } = useTranslationWithPrefix('views.contactsCreate.customerDetails');
  const { isGetting, supplierDetails } = useGetSupplierDetails({ supplierId });

  if (isGetting) {
    return <Loader />;
  }

  return (
    <>
      <Typography sx={{ color: themeColors.greyDark }} variant="f16Regular">
        {t('addingResourceFor', { forResource: t('forSupplier') })}
      </Typography>
      <Typography sx={{ color: themeColors.greyDark, pl: 1 }} variant="f16Medium">
        {supplierDetails?.name}
      </Typography>
    </>
  );
};
